import { PAYLOAD_PLACEHOLDER_REGEX } from '../types/payloadTypes';

/**
 * Scans all string values in a configuration object (recursively including
 * nested objects and arrays) and returns the sorted, deduplicated list of
 * §N§ placeholder IDs found.
 *
 * Fields listed in `excludeKeys` are skipped (e.g. payload_functions itself).
 */
export function scanPayloadPlaceholders(
  config: Record<string, any>,
  excludeKeys: string[] = ['payload_functions', 'cache_busting_params'],
): number[] {
  const found = new Set<number>();

  function scan(val: unknown, key?: string): void {
    if (key !== undefined && excludeKeys.includes(key)) return;
    if (typeof val === 'string') {
      const regex = new RegExp(PAYLOAD_PLACEHOLDER_REGEX.source, 'g');
      let m: RegExpExecArray | null;
      while ((m = regex.exec(val)) !== null) {
        found.add(parseInt(m[1], 10));
      }
    } else if (Array.isArray(val)) {
      val.forEach((item) => scan(item));
    } else if (val && typeof val === 'object') {
      Object.entries(val as Record<string, unknown>).forEach(([k, v]) => scan(v, k));
    }
  }

  Object.entries(config).forEach(([k, v]) => scan(v, k));
  return Array.from(found).sort((a, b) => a - b);
}

/**
 * Inserts `§N§` at the cursor position of a text input/textarea.
 * Returns the new full string value.
 */
export function insertPlaceholderAtCursor(
  element: HTMLInputElement | HTMLTextAreaElement,
  n: number,
): string {
  const placeholder = `§${n}§`;
  const start = element.selectionStart ?? element.value.length;
  const end = element.selectionEnd ?? element.value.length;
  const before = element.value.slice(0, start);
  const after = element.value.slice(end);
  const newValue = before + placeholder + after;

  // Restore cursor position after React re-renders via a microtask
  const newCursor = start + placeholder.length;
  requestAnimationFrame(() => {
    try {
      element.setSelectionRange(newCursor, newCursor);
    } catch {
      // some input types don't support selection
    }
  });

  return newValue;
}
